import React, { useCallback, useEffect, useRef, useState } from "react";
import api, { downloadFile, importFile } from "@/lib/api";
import { rupiah, formatDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PaymentDialog } from "@/components/PaymentDialog";
import { Wallet, AlertTriangle, Clock, Download, Upload, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

const BUCKET_META = {
  overdue: { label: "Lewat Tempo", cls: "bg-rose-100 text-rose-800 border-rose-300" },
  due_soon: { label: "≤ 7 Hari", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  current: { label: "Belum Tempo", cls: "bg-slate-100 text-slate-700 border-slate-300" },
};
const STATUS_CLS = { "Belum Bayar": "bg-slate-100 text-slate-700", "DP": "bg-blue-100 text-blue-800", "Cicilan": "bg-amber-100 text-amber-800", "Lunas": "bg-emerald-100 text-emerald-800", "Overpaid": "bg-purple-100 text-purple-800" };

export default function Receivables() {
  const [data, setData] = useState(null);
  const [full, setFull] = useState(null);
  const [bucket, setBucket] = useState("all");
  const [tab, setTab] = useState("daftar");
  const [payInv, setPayInv] = useState(null);

  const load = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([api.get("/receivables"), api.get("/reports/receivables-full")]);
      setData(a.data); setFull(b.data);
    } catch (e) { console.error("Gagal memuat piutang:", e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const fileRef = useRef(null);
  const doImport = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    try {
      const r = await importFile("/receivables/import", f);
      toast.success(`Import selesai: ${r.data.created} piutang ditambah, ${r.data.skipped} dilewati`);
      if (r.data.errors?.length) toast.warning(r.data.errors.join("; "));
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal import"); }
  };

  const items = (data?.items || []).filter(i => bucket === "all" || i.bucket === bucket);

  return (
    <div data-testid="page-receivables" className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Keuangan</div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Piutang & Pembayaran</h1>
          <p className="text-slate-500 text-sm mt-1">Nota belum lunas, umur piutang, dan riwayat pembayaran DP/cicilan.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => downloadFile("/receivables/template", "template_piutang.xlsx")} data-testid="btn-template"><FileSpreadsheet className="h-4 w-4 mr-1" />Template</Button>
          <Button variant="outline" size="sm" onClick={() => downloadFile("/receivables/export", "rekapan_piutang.xlsx")} data-testid="btn-export"><Download className="h-4 w-4 mr-1" />Export</Button>
          <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="btn-import"><Upload className="h-4 w-4 mr-1" />Import</Button>
          <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={doImport} data-testid="input-import" />
        </div>
      </div>

      {data && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <SumCard icon={<Wallet />} label="Total Piutang" value={rupiah(data.total_outstanding)} sub={`${data.count} nota`} accent="bg-slate-900 text-white" testid="recv-total" />
          <SumCard icon={<AlertTriangle />} label="Lewat Jatuh Tempo" value={rupiah(data.overdue_total)} accent="bg-rose-500 text-white" testid="recv-overdue" />
          <SumCard icon={<Clock />} label="Jatuh Tempo ≤ 7 Hari" value={rupiah(data.due_soon_total)} accent="bg-amber-500 text-white" testid="recv-due-soon" />
          <SumCard icon={<Wallet />} label="Belum Jatuh Tempo" value={rupiah(data.total_outstanding - data.overdue_total - data.due_soon_total)} accent="bg-emerald-500 text-white" testid="recv-current" />
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="recv-tabs">
          <TabsTrigger value="daftar" data-testid="tab-daftar">Invoice Belum Lunas</TabsTrigger>
          <TabsTrigger value="customer" data-testid="tab-per-customer">Per Pelanggan</TabsTrigger>
          <TabsTrigger value="aging" data-testid="tab-aging">Umur Piutang</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">Riwayat Pembayaran</TabsTrigger>
        </TabsList>

        <TabsContent value="daftar" className="space-y-4">
          <Tabs value={bucket} onValueChange={setBucket}>
            <TabsList data-testid="bucket-tabs">
              <TabsTrigger value="all" data-testid="bucket-all">Semua</TabsTrigger>
              <TabsTrigger value="overdue" data-testid="bucket-overdue">Lewat Tempo</TabsTrigger>
              <TabsTrigger value="due_soon" data-testid="bucket-due-soon">≤ 7 Hari</TabsTrigger>
              <TabsTrigger value="current" data-testid="bucket-current">Belum Tempo</TabsTrigger>
            </TabsList>
          </Tabs>
          <Card>
            <table className="w-full text-sm" data-testid="table-receivables">
              <thead className="bg-slate-50 text-xs font-mono uppercase tracking-wider text-slate-500 text-left">
                <tr><th className="py-2 px-4">No. Invoice</th><th>Tanggal</th><th>Pelanggan</th><th>Sales</th><th>Jatuh Tempo</th><th>Sisa Hari</th><th>Status</th><th className="text-right">Total</th><th className="text-right">Terbayar</th><th className="text-right">Sisa Tagihan</th><th></th></tr>
              </thead>
              <tbody>
                {items.map(r => (
                  <tr key={r.invoice_id} className="border-b">
                    <td className="py-2 px-4 font-mono text-xs">{r.trx_no}</td>
                    <td>{formatDate(r.date)}</td>
                    <td className="font-medium">{r.customer}</td>
                    <td>{r.sales_name}</td>
                    <td className="font-mono text-xs">{formatDate(r.due_date)}</td>
                    <td className={`font-mono text-xs ${r.days < 0 ? "text-rose-700 font-bold" : r.days <= 7 ? "text-amber-700" : "text-slate-500"}`}>
                      {r.days < 0 ? `Lewat ${-r.days} hari` : `${r.days} hari lagi`}
                    </td>
                    <td><Badge className={`${BUCKET_META[r.bucket].cls} font-normal`}>{BUCKET_META[r.bucket].label}</Badge></td>
                    <td className="text-right font-mono">{rupiah(r.total)}</td>
                    <td className="text-right font-mono text-emerald-700">{r.paid_amount ? rupiah(r.paid_amount) : "-"}</td>
                    <td className="text-right font-mono font-bold">{rupiah(r.remaining)}</td>
                    <td><Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setPayInv(r)} data-testid={`pay-${r.trx_no}`}>Bayar / Cicil</Button></td>
                  </tr>
                ))}
                {items.length === 0 && <tr><td colSpan={11} className="py-10 text-center text-slate-400">Tidak ada piutang pada kategori ini</td></tr>}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="customer">
          <Card>
            <table className="w-full text-sm" data-testid="table-recv-customer">
              <thead className="bg-slate-50 text-xs font-mono uppercase tracking-wider text-slate-500 text-left">
                <tr><th className="py-2 px-4">Pelanggan</th><th className="text-right">Nota</th><th className="text-right">Total Tagihan</th><th className="text-right">Terbayar</th><th className="text-right">Sisa Piutang</th></tr>
              </thead>
              <tbody>
                {(full?.per_customer || []).map(c => (
                  <tr key={c.customer} className="border-b">
                    <td className="py-2 px-4 font-medium">{c.customer}</td>
                    <td className="text-right font-mono">{c.invoice_count}</td>
                    <td className="text-right font-mono">{rupiah(c.total)}</td>
                    <td className="text-right font-mono text-emerald-700">{rupiah(c.paid)}</td>
                    <td className="text-right font-mono font-bold text-rose-700">{rupiah(c.remaining)}</td>
                  </tr>
                ))}
                {(full?.per_customer || []).length === 0 && <tr><td colSpan={5} className="py-10 text-center text-slate-400">Tidak ada piutang</td></tr>}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="aging">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="aging-cards">
            {["0-30", "31-60", "61-90", ">90"].map((b) => (
              <Card key={b} className="p-5">
                <div className="text-xs font-mono uppercase tracking-wider text-slate-500">Umur {b} hari</div>
                <div className="font-mono font-bold text-xl mt-1" data-testid={`aging-${b}`}>{rupiah(full?.aging?.[b] || 0)}</div>
                <div className="text-xs text-slate-500">{full?.aging_count?.[b] || 0} nota</div>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="payments">
          <Card>
            <table className="w-full text-sm" data-testid="table-payments">
              <thead className="bg-slate-50 text-xs font-mono uppercase tracking-wider text-slate-500 text-left">
                <tr><th className="py-2 px-4">Tanggal</th><th>No. Invoice</th><th>Pelanggan</th><th>Sales</th><th>Jenis</th><th>Metode</th><th>Ref</th><th className="text-right">Nominal</th></tr>
              </thead>
              <tbody>
                {(full?.payments || []).map(p => (
                  <tr key={p.payment_id} className="border-b">
                    <td className="py-2 px-4 font-mono text-xs">{formatDate(p.pay_date)}</td>
                    <td className="font-mono text-xs">{p.trx_no}</td>
                    <td>{p.customer}</td>
                    <td>{p.sales_name}</td>
                    <td><Badge className={`${STATUS_CLS[p.jenis] || "bg-slate-100 text-slate-700"} font-normal`}>{p.jenis}</Badge></td>
                    <td>{p.metode}</td>
                    <td className="font-mono text-xs text-slate-400">{p.ref_no || "-"}</td>
                    <td className="text-right font-mono">{rupiah(p.amount)}</td>
                  </tr>
                ))}
                {(full?.payments || []).length === 0 && <tr><td colSpan={8} className="py-10 text-center text-slate-400">Belum ada pembayaran tercatat</td></tr>}
              </tbody>
            </table>
          </Card>
        </TabsContent>
      </Tabs>
      <PaymentDialog invoice={payInv} onClose={() => setPayInv(null)} onDone={load} />
    </div>
  );
}

function SumCard({ icon, label, value, sub, accent, testid }) {
  return (
    <div data-testid={testid} className="bg-white rounded-lg border border-slate-200 p-5">
      <div className={`h-10 w-10 rounded grid place-items-center mb-4 ${accent}`}>{React.cloneElement(icon, { className: "h-5 w-5" })}</div>
      <div className="text-xs font-mono uppercase tracking-wider text-slate-500">{label}</div>
      <div className="font-mono font-bold text-xl text-slate-900 mt-1">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}
